import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Rocket, Loader2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// Compact, mobile-first Hail Mary section for the Make Picks page.
// Renders only during an ACTIVE regular-season week.
// Game-first flow: pick GAME -> QB (limited to that game's two teams) -> yards -> TDs.
export const HailMaryPanel = ({ preview = false, isAdmin = false }) => {
  const [hm, setHm] = useState(null);
  const [games, setGames] = useState([]);
  const [gameQbs, setGameQbs] = useState([]);
  const [modal, setModal] = useState(null); // {step:'confirm'|'entry'}
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ game_id: "", qb_id: "", qb_name: "", qb_team: "", yard_range: "", td_range: "" });
  const [busy, setBusy] = useState(false);
  const [grantN, setGrantN] = useState(3);

  const PREVIEW_GAMES = [
    { game_id: "pg1", home: "BUF", away: "NE", label: "NE @ BUF", is_locked: false },
    { game_id: "pg2", home: "KC", away: "DEN", label: "DEN @ KC", is_locked: false },
    { game_id: "pg3", home: "BAL", away: "PIT", label: "PIT @ BAL", is_locked: false },
    { game_id: "pg4", home: "PHI", away: "DAL", label: "DAL @ PHI", is_locked: false },
  ];
  const PREVIEW_QBS_BY_GAME = {
    pg1: [{ id: "buf1", name: "Josh Allen", team: "BUF", game_id: "pg1" }, { id: "ne1", name: "Drake Maye", team: "NE", game_id: "pg1" }],
    pg2: [{ id: "kc1", name: "Patrick Mahomes", team: "KC", game_id: "pg2" }, { id: "den1", name: "Bo Nix", team: "DEN", game_id: "pg2" }],
    pg3: [{ id: "bal1", name: "Lamar Jackson", team: "BAL", game_id: "pg3" }, { id: "pit1", name: "Russell Wilson", team: "PIT", game_id: "pg3" }],
    pg4: [{ id: "phi1", name: "Jalen Hurts", team: "PHI", game_id: "pg4" }, { id: "dal1", name: "Dak Prescott", team: "DAL", game_id: "pg4" }],
  };
  const previewBase = () => ({
    active: true, week: 15, week_label: "Regular Season Week 15 (PREVIEW)",
    cards_total: 3, cards_remaining: 3, value: { both: 20, one: 10, miss: 0 },
    yard_ranges: ["Under 150", "150-199", "200-249", "250-299", "300-349", "350-399", "400+"],
    td_ranges: ["0", "1-2", "3-4", "5+"], cards: [],
  });

  const load = async () => {
    if (preview) { setHm(previewBase()); setGames(PREVIEW_GAMES); return; }
    try { const { data } = await api.get("/showdown/hailmary"); setHm(data); } catch { /* optional */ }
    try { const { data } = await api.get("/showdown/hailmary/games"); setGames(data.games || []); } catch { /* optional */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preview]);

  const chooseGame = async (gid) => {
    setForm((f) => ({ ...f, game_id: gid, qb_id: "", qb_name: "", qb_team: "" }));
    setGameQbs([]);
    if (!gid) return;
    if (preview) { setGameQbs(PREVIEW_QBS_BY_GAME[gid] || []); return; }
    try { const { data } = await api.get(`/showdown/hailmary/qbs?game_id=${gid}`); setGameQbs(data.qbs || []); }
    catch { setGameQbs([]); }
  };

  const chooseQb = (val) => {
    const q = gameQbs.find((x) => x.id === val);
    if (q) setForm((f) => ({ ...f, qb_id: q.id, qb_name: q.name, qb_team: q.team, game_id: q.game_id }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ game_id: "", qb_id: "", qb_name: "", qb_team: "", yard_range: "", td_range: "" });
    setGameQbs([]);
    setModal({ step: "confirm" });
  };

  const openEdit = async (card) => {
    setEditingId(card.id);
    setForm({ game_id: card.game_id, qb_id: card.qb_id, qb_name: card.qb_name, qb_team: card.qb_team,
      yard_range: card.yard_range, td_range: card.td_range });
    if (preview) setGameQbs(PREVIEW_QBS_BY_GAME[card.game_id] || []);
    else { try { const { data } = await api.get(`/showdown/hailmary/qbs?game_id=${card.game_id}`); setGameQbs(data.qbs || []); } catch { /* */ } }
    setModal({ step: "entry" });
  };

  const save = async () => {
    if (preview) {
      if (editingId) {
        setHm((h) => ({ ...h, cards: h.cards.map((c) => (c.id === editingId ? { ...c, ...form } : c)) }));
        toast.success("Preview Hail Mary updated (test only — card count unchanged)");
      } else {
        setHm((h) => ({ ...h, cards: [...h.cards, { id: "prev-" + Date.now(), status: "reserved", ...form }],
          cards_remaining: h.cards_remaining - 1 }));
        toast.success("Preview Hail Mary saved (test only — no real card used)");
      }
      setModal(null); return;
    }
    setBusy(true);
    try {
      if (editingId) { await api.put(`/showdown/hailmary/${editingId}`, form); toast.success("Hail Mary updated!"); }
      else { await api.post("/showdown/hailmary", form); toast.success("Hail Mary saved!"); }
      setModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancelCard = async (id) => {
    if (preview) {
      setHm((h) => ({ ...h, cards: h.cards.filter((c) => c.id !== id), cards_remaining: h.cards_remaining + 1 }));
      toast.success("Preview card returned — game released"); return;
    }
    try { await api.delete(`/showdown/hailmary/${id}`); toast.success("Hail Mary cancelled — card returned, game released."); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const voidCard = async (id) => {
    if (!window.confirm("Void this Hail Mary and return the card to the player? This cannot be undone.")) return;
    if (preview) {
      setHm((h) => ({ ...h, cards: h.cards.map((c) => (c.id === id ? { ...c, status: "void" } : c)),
        cards_remaining: h.cards_remaining + 1 }));
      toast.success("Preview card voided — game released (test only)"); return;
    }
    try { await api.post(`/showdown/hailmary/${id}/void`); toast.success("Hail Mary voided — card returned, game released."); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const previewGrant = () => {
    const n = parseInt(grantN, 10);
    if (!n || n < 1) { toast.error("Enter a number of cards to grant"); return; }
    if (!window.confirm(`Grant ${n} additional Hail Mary card${n > 1 ? "s" : ""} to this player (preview test — no real change)?`)) return;
    setHm((h) => ({ ...h, cards_total: (h.cards_total || 0) + n, cards_remaining: h.cards_remaining + n }));
    toast.success(`Preview: +${n} bonus card${n > 1 ? "s" : ""} granted (test only — no database writes)`);
  };

  if (!hm || !hm.active) return null; // inactive during preseason
  const val = hm.value;
  const ready = form.game_id && form.qb_id && form.yard_range && form.td_range;
  const gameLabel = (gid) => (games.find((g) => g.game_id === gid)?.label) || "";

  const usedGameIds = new Set((hm.cards || []).filter((c) => c.status !== "void").map((c) => c.game_id));
  // Games selectable in the modal: not locked, not already used by this player (but keep the card being edited's game)
  const selectableGames = games.filter((g) => !g.is_locked && (!usedGameIds.has(g.game_id) || g.game_id === form.game_id));
  // Fresh games available for a NEW card
  const newGames = games.filter((g) => !g.is_locked && !usedGameIds.has(g.game_id));

  return (
    <div className="mt-8 rounded-2xl border border-purple-500/30 bg-purple-500/5 p-6" data-testid="hailmary-panel">
      <div className="flex items-center gap-2">
        <Rocket className="h-4 w-4 text-purple-400" />
        <span className="overline text-purple-300">Hail Mary · {hm.week_label}</span>
        <span className="ml-auto text-xs font-bold text-purple-200" data-testid="hm-cards-remaining">Cards Remaining: {hm.cards_remaining}</span>
      </div>
      <p className="text-zinc-400 text-sm mt-2">High-risk bonus. This week: <span className="font-bold text-purple-300">Hit both {val.both} pts · Hit one {val.one} pts</span>. One Hail Mary per game.</p>
      {typeof hm.cards_total === "number" && (
        <p className="text-xs text-zinc-500 mt-1" data-testid="hm-cards-total">Seasonal allocation: <span className="font-semibold text-purple-200">{hm.cards_total}</span> cards</p>
      )}

      {preview && isAdmin && (
        <div className="mt-3 flex items-center gap-2 flex-wrap rounded-lg border border-purple-500/25 bg-purple-500/5 p-3" data-testid="hm-preview-grant">
          <span className="text-xs font-bold uppercase text-purple-200">Grant bonus (preview)</span>
          <input type="number" min="1" max="20" value={grantN} onChange={(e) => setGrantN(e.target.value)}
            data-testid="hm-preview-grant-input"
            className="w-16 rounded-lg bg-[#121212] border border-[#1F1F22] px-2 py-1.5 text-sm text-center" />
          <button data-testid="hm-preview-grant-btn" onClick={previewGrant}
            className="px-3 py-1.5 rounded-full bg-purple-600 text-white text-xs font-bold active:scale-95 transition-transform">
            Grant cards
          </button>
          <span className="text-[11px] text-zinc-500">mock only — no DB writes</span>
        </div>
      )}

      {hm.cards.filter((c) => c.status !== "void").map((c, i) => (
        <div key={c.id} data-testid={`hm-card-${i}`} className="mt-3 rounded-xl border border-[#1F1F22] bg-[#0A0A0A] p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-bold">Hail Mary #{i + 1}: {c.qb_name} <span className="text-zinc-500">({c.qb_team})</span></span>
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300">{c.status}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {gameLabel(c.game_id) && <span className="text-zinc-400">{gameLabel(c.game_id)} · </span>}
            {c.yard_range} yds · {c.td_range} TD · potential {val.both}/{val.one}
          </div>
          <div className="flex items-center gap-3 mt-2">
            {c.status === "reserved" && (
              <>
                <button data-testid={`hm-edit-${i}`} onClick={() => openEdit(c)} className="inline-flex items-center gap-1 text-xs text-[#4C8DFF] hover:text-[#7ab0ff] font-semibold">
                  <Pencil className="h-3 w-3" /> Edit Hail Mary
                </button>
                <button data-testid={`hm-cancel-${i}`} onClick={() => cancelCard(c.id)} className="text-xs text-red-400 hover:text-red-300 font-semibold">Cancel &amp; return card</button>
              </>
            )}
            {isAdmin && c.status !== "void" && (
              <button data-testid={`hm-void-${i}`} onClick={() => voidCard(c.id)} className="text-xs text-amber-400 hover:text-amber-300 font-semibold">Void Hail Mary / Return Card</button>
            )}
          </div>
        </div>
      ))}

      {hm.cards_remaining > 0 && newGames.length > 0 && (
        <button data-testid="use-hailmary-btn" onClick={openCreate}
          className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-purple-600 text-white font-bold hover:shadow-[0_0_15px_rgba(147,51,234,0.4)] active:scale-95 transition-transform">
          <Rocket className="h-4 w-4" /> {hm.cards.some((c) => c.status !== "void") ? "Use Another Hail Mary" : "Use Hail Mary"}
        </button>
      )}
      {hm.cards_remaining > 0 && newGames.length === 0 && (
        <p className="mt-4 text-xs text-zinc-500" data-testid="hm-no-games">No more games available this week — you already have a Hail Mary on every remaining game.</p>
      )}

      {modal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" data-testid="hm-modal">
          <div className="w-full sm:max-w-md bg-[#0A0A0A] border border-purple-500/40 rounded-t-2xl sm:rounded-2xl p-6 max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-black text-lg">{editingId ? "Edit your Hail Mary" : "You're using a Hail Mary"}</span>
              <button data-testid="hm-modal-close" onClick={() => setModal(null)}><X className="h-5 w-5 text-zinc-400" /></button>
            </div>
            {modal.step === "confirm" ? (
              <>
                <p className="text-sm text-zinc-400">You have a limited number of Hail Mary cards for the entire regular season. You may use multiple in the same week, but each must be on a different game. This can be changed or cancelled until that game kicks off.</p>
                <p className="text-sm mt-3">Cards currently remaining: <span className="font-bold text-purple-300">{hm.cards_remaining}</span></p>
                <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-3 mt-3 text-sm">
                  Hail Mary — {hm.week_label}<br />Hit both: <b>{val.both} pts</b> · Hit one: <b>{val.one} pts</b> · Miss both: <b>0</b>
                </div>
                <div className="flex gap-2 mt-4">
                  <button data-testid="hm-continue" onClick={() => setModal({ step: "entry" })} className="flex-1 py-3 rounded-full bg-purple-600 text-white font-bold">Continue</button>
                  <button data-testid="hm-cancel-modal" onClick={() => setModal(null)} className="flex-1 py-3 rounded-full border border-[#1F1F22] font-semibold">Cancel</button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-300">Game</label>
                  <select data-testid="hm-game-select" value={form.game_id} onChange={(e) => chooseGame(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2.5 text-sm">
                    <option value="">Select a game…</option>
                    {selectableGames.map((g) => <option key={g.game_id} value={g.game_id}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-300">Quarterback</label>
                  <select data-testid="hm-qb-select" value={form.qb_id} disabled={!form.game_id} onChange={(e) => chooseQb(e.target.value)}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2.5 text-sm disabled:opacity-50">
                    <option value="">{form.game_id ? "Select a QB…" : "Select a game first"}</option>
                    {gameQbs.map((q) => <option key={q.id + q.game_id} value={q.id}>{q.name} ({q.team})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-300">Passing yards</label>
                  <select data-testid="hm-yards-select" value={form.yard_range} disabled={!form.qb_id} onChange={(e) => setForm((f) => ({ ...f, yard_range: e.target.value }))}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2.5 text-sm disabled:opacity-50">
                    <option value="">Select range…</option>
                    {hm.yard_ranges.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-zinc-300">Passing TDs</label>
                  <select data-testid="hm-tds-select" value={form.td_range} disabled={!form.qb_id} onChange={(e) => setForm((f) => ({ ...f, td_range: e.target.value }))}
                    className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2.5 text-sm disabled:opacity-50">
                    <option value="">Select range…</option>
                    {hm.td_ranges.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {ready && (
                  <div className="rounded-lg bg-[#121212] border border-[#1F1F22] p-3 text-sm">
                    <b>{gameLabel(form.game_id)}</b> · <b>{form.qb_name}</b> ({form.qb_team}) · {form.yard_range} yds · {form.td_range} TD<br />
                    Potential this week: <b className="text-purple-300">{val.both}</b> both / <b className="text-purple-300">{val.one}</b> one
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button data-testid="hm-save" onClick={save} disabled={!ready || busy}
                    className="flex-1 py-3 rounded-full bg-purple-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editingId ? "Update Hail Mary" : "Save Hail Mary"}
                  </button>
                  <button onClick={() => setModal(null)} className="flex-1 py-3 rounded-full border border-[#1F1F22] font-semibold">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
